import { useEffect, useMemo, useRef } from "react";
import useRouteHighlight from "./useRouteHighlight.js";
import useRouteMap from "./useRouteMap.js";
import useRouteSelectionSync from "./useRouteSelectionSync.js";

const ROUTE_MAP_DOUBLE_CLICK_ZOOM_LEVEL = 13;
const ROUTE_MAP_BADGE_Z_INDEX_OFFSET = 200;
const ROUTE_MAP_ACTIVE_BADGE_Z_INDEX_OFFSET = 1200;
const ROUTE_MAP_ACTIVE_BADGE_TRANSLATE = "0px -18px";
const ROUTE_MAP_ACTIVE_BADGE_FILTER = "drop-shadow(0 8px 18px rgba(15, 92, 87, 0.28))";

const EMPTY_CONTROLLER = {
  clearHighlight() {},
  highlightClient() {},
  highlightRouteSegment() {},
  highlightRouteSegments() {},
  restoreView() {
    return false;
  },
  toggleRouteNumber() {
    return false;
  },
  zoomToRouteNumber() {
    return false;
  },
  zoomToClient() {
    return false;
  },
};

function hasTrackedBadgeMarker(mapState, marker) {
  if (!mapState?.map || mapState.isDisposed || !marker) {
    return false;
  }

  return mapState.stopBadgeLayer?.hasLayer?.(marker) === true || mapState.map.hasLayer?.(marker) === true;
}

function resolveClientBadgeMarker(mapState, clientId = "", stopType = "") {
  if (!mapState?.map || mapState.isDisposed || !Array.isArray(mapState.badgeMarkers) || !clientId) {
    return null;
  }

  let fallbackMarker = null;
  for (const marker of mapState.badgeMarkers) {
    if (!hasTrackedBadgeMarker(mapState, marker) || marker.__clientId !== clientId) {
      continue;
    }

    if (!fallbackMarker) {
      fallbackMarker = marker;
    }

    if (!stopType || !marker.__stopType || marker.__stopType === stopType) {
      return marker;
    }
  }

  return fallbackMarker;
}

function resolveRouteNumberBadgeMarker(mapState, routeNumber = null) {
  const normalizedRouteNumber = Number.parseInt(routeNumber, 10);
  if (!mapState?.map || mapState.isDisposed || !Array.isArray(mapState.badgeMarkers) || !Number.isInteger(normalizedRouteNumber)) {
    return null;
  }

  return mapState.badgeMarkers.find((marker) => {
    return hasTrackedBadgeMarker(mapState, marker) && marker.__routeNumber === normalizedRouteNumber;
  }) || null;
}

function resolveBadgeMarkerElement(marker) {
  return marker?.getElement?.() || marker?._icon || null;
}

function getRouteMapBadgeElements(mapState) {
  const mapContainer = typeof mapState?.map?.getContainer === "function"
    ? mapState.map.getContainer()
    : null;
  return mapContainer && typeof mapContainer.querySelectorAll === "function"
    ? Array.from(mapContainer.querySelectorAll(".route-map-stop-index"))
    : [];
}

function hasRouteMapBadgeAnchor(markerElement, routeNumber = null) {
  const normalizedRouteNumber = Number.parseInt(routeNumber, 10);
  return Number.isInteger(normalizedRouteNumber)
    && markerElement instanceof HTMLElement
    && markerElement.classList.contains(`route-map-stop-index--anchor-${normalizedRouteNumber}`);
}

function clearFocusedBadgeMarkers(mapState) {
  if (!mapState?.map || mapState.isDisposed || !Array.isArray(mapState.badgeMarkers)) {
    return;
  }

  mapState.badgeMarkers.forEach((marker) => {
    if (!hasTrackedBadgeMarker(mapState, marker)) {
      return;
    }

    marker.closeTooltip?.();
    marker.setZIndexOffset?.(ROUTE_MAP_BADGE_Z_INDEX_OFFSET);

    const markerElement = resolveBadgeMarkerElement(marker);
    if (markerElement && typeof markerElement.style === "object") {
      markerElement.style.zIndex = "";
      markerElement.style.translate = "";
      markerElement.style.filter = "";
      markerElement.style.opacity = "";
      markerElement.style.visibility = "";
      markerElement.style.pointerEvents = "";
    }
  });

  getRouteMapBadgeElements(mapState).forEach((markerElement) => {
    if (markerElement && typeof markerElement.style === "object") {
      markerElement.style.zIndex = "";
      markerElement.style.translate = "";
      markerElement.style.filter = "";
      markerElement.style.opacity = "";
      markerElement.style.visibility = "";
      markerElement.style.pointerEvents = "";
    }
  });
}

function hideOverlappingBadgeMarkers(mapState, activeMarker) {
  if (!mapState?.map || mapState.isDisposed || !activeMarker || !Array.isArray(mapState.badgeMarkers)) {
    return;
  }

  if (mapState.stopBadgePresentation === "icon") {
    return;
  }

  const activeRouteNumber = Number.isInteger(activeMarker?.__routeNumber) ? activeMarker.__routeNumber : null;

  mapState.badgeMarkers.forEach((marker) => {
    if (!hasTrackedBadgeMarker(mapState, marker) || marker === activeMarker) {
      return;
    }

    marker.closeTooltip?.();
    marker.setZIndexOffset?.(ROUTE_MAP_BADGE_Z_INDEX_OFFSET);

    const markerElement = resolveBadgeMarkerElement(marker);
    if (markerElement && typeof markerElement.style === "object") {
      markerElement.style.opacity = "0";
      markerElement.style.visibility = "hidden";
      markerElement.style.pointerEvents = "none";
    }
  });

  getRouteMapBadgeElements(mapState).forEach((markerElement) => {
    if (!markerElement || typeof markerElement.style !== "object") {
      return;
    }

    if (hasRouteMapBadgeAnchor(markerElement, activeRouteNumber)) {
      return;
    }

    markerElement.style.opacity = "0";
    markerElement.style.visibility = "hidden";
    markerElement.style.pointerEvents = "none";
  });
}

function emphasizeBadgeMarker(mapState, marker) {
  if (!mapState?.map || mapState.isDisposed || !marker) {
    return;
  }

  clearFocusedBadgeMarkers(mapState);
  hideOverlappingBadgeMarkers(mapState, marker);
  marker.setZIndexOffset?.(ROUTE_MAP_ACTIVE_BADGE_Z_INDEX_OFFSET);
  const routeNumber = Number.isInteger(marker?.__routeNumber) ? marker.__routeNumber : null;

  const markerElement = resolveBadgeMarkerElement(marker);
  if (markerElement && typeof markerElement.style === "object") {
    markerElement.style.zIndex = String(ROUTE_MAP_ACTIVE_BADGE_Z_INDEX_OFFSET);
    markerElement.style.translate = ROUTE_MAP_ACTIVE_BADGE_TRANSLATE;
    markerElement.style.filter = ROUTE_MAP_ACTIVE_BADGE_FILTER;
  }

  if (routeNumber != null) {
    getRouteMapBadgeElements(mapState).forEach((candidateElement) => {
      if (!hasRouteMapBadgeAnchor(candidateElement, routeNumber)) {
        return;
      }

      if (candidateElement && typeof candidateElement.style === "object") {
        candidateElement.style.zIndex = String(ROUTE_MAP_ACTIVE_BADGE_Z_INDEX_OFFSET);
        candidateElement.style.translate = ROUTE_MAP_ACTIVE_BADGE_TRANSLATE;
        candidateElement.style.filter = ROUTE_MAP_ACTIVE_BADGE_FILTER;
      }
    });
  }
}

function focusClientBadgeMarker(mapState, badgeMarker, fallbackMarker, focusRequestId = 0) {
  if (!mapState?.map || mapState.isDisposed) {
    return false;
  }

  const fallbackLatLng = badgeMarker?.getLatLng?.() || fallbackMarker?.getLatLng?.();
  const badgeLayer = mapState.stopBadgeLayer || null;
  if (badgeMarker && typeof badgeLayer?.zoomToShowLayer === "function") {
    badgeLayer.zoomToShowLayer(badgeMarker, () => {
      if ((mapState.badgeFocusRequestId || 0) !== focusRequestId) {
        return;
      }

      emphasizeBadgeMarker(mapState, badgeMarker);
      if (!fallbackLatLng || mapState.map.getZoom() >= ROUTE_MAP_DOUBLE_CLICK_ZOOM_LEVEL) {
        return;
      }

      mapState.map.setView(fallbackLatLng, ROUTE_MAP_DOUBLE_CLICK_ZOOM_LEVEL, { animate: true });
    });
    return true;
  }

  if (!fallbackLatLng) {
    return false;
  }

  const nextZoom = Math.max(mapState.map.getZoom(), ROUTE_MAP_DOUBLE_CLICK_ZOOM_LEVEL);
  mapState.map.setView(fallbackLatLng, nextZoom, { animate: true });
  if ((mapState.badgeFocusRequestId || 0) !== focusRequestId) {
    return false;
  }

  emphasizeBadgeMarker(mapState, badgeMarker);
  return true;
}

function resolveClientMarker(mapState, clientId = "", stopType = "") {
  if (!mapState?.map || mapState.isDisposed || !Array.isArray(mapState.markers) || !clientId) {
    return null;
  }

  let fallbackMarker = null;
  for (const marker of mapState.markers) {
    if (!marker || mapState.map.hasLayer?.(marker) !== true || marker.__clientId !== clientId) {
      continue;
    }

    if (!fallbackMarker) {
      fallbackMarker = marker;
    }

    if (!stopType || !marker.__stopType || marker.__stopType === stopType) {
      return marker;
    }
  }

  return fallbackMarker;
}

function restoreMapBaseView(mapState) {
  if (!mapState?.map || mapState.isDisposed || !mapState.baseView) {
    return false;
  }

  if (mapState.baseView.mode === "bounds" && Array.isArray(mapState.baseView.bounds) && mapState.baseView.bounds.length) {
    if (!window.L?.latLngBounds) {
      return false;
    }

    mapState.map.fitBounds(window.L.latLngBounds(mapState.baseView.bounds), {
      animate: true,
      padding: Array.isArray(mapState.baseView.padding) ? mapState.baseView.padding : [28, 28],
    });
    return true;
  }

  if (Array.isArray(mapState.baseView.center) && mapState.baseView.center.length === 2) {
    mapState.map.setView(mapState.baseView.center, mapState.baseView.zoom, { animate: true });
    return true;
  }

  return false;
}

export default function useRouteMapController({
  assignment = null,
  isRouteViewActive = true,
  onClientSelect = null,
  onControllerReady = null,
  onMapRenderMetaChange = null,
  onRouteHighlightChange = null,
  routeAlternativePreview = null,
  routeDistanceLabel = "",
  selectedClientId = null,
  selectedStopType = "",
  selectionRequest = null,
  showGroupedStopRangeLabels = false,
  stopBadgePresentation = "number",
  stateRef,
  trip = null,
}) {
  const {
    mapContainerRef,
    mapRenderMeta,
    mapStateRef,
    routeStatus,
    statusNotice,
  } = useRouteMap({
    assignment,
    isRouteViewActive,
    onClientSelect,
    routeAlternativePreview,
    routeDistanceLabel,
    showGroupedStopRangeLabels,
    stopBadgePresentation,
    stateRef,
    trip,
  });

  const routeHighlight = useRouteHighlight({
    assignment,
    mapStateRef,
    onHighlightChange: onRouteHighlightChange,
    trip,
  });
  const routeHighlightRef = useRef(routeHighlight);
  const activeRouteNumberRef = useRef(null);
  const badgeFocusRequestRef = useRef(0);

  useEffect(() => {
    routeHighlightRef.current = routeHighlight;
  }, [routeHighlight]);

  useEffect(() => {
    activeRouteNumberRef.current = null;
    badgeFocusRequestRef.current += 1;
    if (mapStateRef.current) {
      mapStateRef.current.badgeFocusRequestId = badgeFocusRequestRef.current;
    }
    clearFocusedBadgeMarkers(mapStateRef.current || null);
  }, [assignment, trip]);

  useRouteSelectionSync({
    mapRenderMeta,
    mapStateRef,
    selectedClientId,
    selectedStopType,
    selectionRequest,
  });

  const controller = useMemo(() => ({
    clearHighlight() {
      routeHighlightRef.current.clearHighlight();
    },
    highlightClient(clientId) {
      routeHighlightRef.current.highlightClient(clientId);
    },
    highlightRouteSegment(routeSegmentIndex) {
      routeHighlightRef.current.highlightRouteSegment(routeSegmentIndex);
    },
    highlightRouteSegments(routeSegmentIndexes) {
      routeHighlightRef.current.highlightRouteSegments(routeSegmentIndexes);
    },
    restoreView() {
      const mapState = mapStateRef.current || null;
      badgeFocusRequestRef.current += 1;
      if (mapState) {
        mapState.badgeFocusRequestId = badgeFocusRequestRef.current;
      }
      const restored = restoreMapBaseView(mapState);
      if (restored) {
        activeRouteNumberRef.current = null;
        clearFocusedBadgeMarkers(mapState);
      }
      return restored;
    },
    toggleRouteNumber(routeNumber) {
      const normalizedRouteNumber = Number.parseInt(routeNumber, 10);
      if (!Number.isInteger(normalizedRouteNumber) || normalizedRouteNumber <= 0) {
        return false;
      }

      if (activeRouteNumberRef.current === normalizedRouteNumber) {
        const restored = restoreMapBaseView(mapStateRef.current || null);
        if (restored) {
          activeRouteNumberRef.current = null;
        }
        return restored;
      }

      const mapState = mapStateRef.current || null;
      const badgeMarker = resolveRouteNumberBadgeMarker(mapState, normalizedRouteNumber);
      if (!badgeMarker || !mapState?.map || mapState.isDisposed) {
        return false;
      }

      badgeFocusRequestRef.current += 1;
      mapState.badgeFocusRequestId = badgeFocusRequestRef.current;
      const zoomed = focusClientBadgeMarker(mapState, badgeMarker, null, badgeFocusRequestRef.current);
      if (zoomed) {
        activeRouteNumberRef.current = normalizedRouteNumber;
      }
      return zoomed;
    },
    zoomToRouteNumber(routeNumber) {
      const mapState = mapStateRef.current || null;
      const normalizedRouteNumber = Number.parseInt(routeNumber, 10);
      const badgeMarker = resolveRouteNumberBadgeMarker(mapState, normalizedRouteNumber);
      if (!badgeMarker || !mapState?.map || mapState.isDisposed) {
        return false;
      }

      badgeFocusRequestRef.current += 1;
      mapState.badgeFocusRequestId = badgeFocusRequestRef.current;
      const zoomed = focusClientBadgeMarker(mapState, badgeMarker, null, badgeFocusRequestRef.current);
      if (zoomed) {
        activeRouteNumberRef.current = normalizedRouteNumber;
      }
      return zoomed;
    },
    zoomToClient(clientId, stopType = "") {
      const mapState = mapStateRef.current || null;
      const marker = resolveClientMarker(mapState, clientId, stopType);
      const badgeMarker = resolveClientBadgeMarker(mapState, clientId, stopType);
      if ((!marker && !badgeMarker) || !mapState?.map || mapState.isDisposed) {
        return false;
      }

      routeHighlightRef.current.highlightClient(clientId);
      badgeFocusRequestRef.current += 1;
      mapState.badgeFocusRequestId = badgeFocusRequestRef.current;
      const zoomed = focusClientBadgeMarker(mapState, badgeMarker, marker, badgeFocusRequestRef.current);
      if (zoomed) {
        activeRouteNumberRef.current = null;
      }
      return zoomed;
    },
  }), []);

  useEffect(() => {
    onMapRenderMetaChange?.(mapRenderMeta || null);
  }, [mapRenderMeta, onMapRenderMetaChange]);

  useEffect(() => {
    onControllerReady?.(controller);

    return () => {
      onControllerReady?.(EMPTY_CONTROLLER);
    };
  }, [controller, onControllerReady]);

  return {
    mapContainerRef,
    controller,
    routeStatus,
    statusNotice,
  };
}
